import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { TRAINING_URL } from '../shared/src/lib/shared';

export interface QuizScheduleRequest {
    id: number;
    title: string;
    date: string;
    startTime: string;
    endTime: string;
    durationId: number;
    qIds: number[];
    /**
     * Optional structured pre/post quiz configuration.
     * Mirrors TrainingSummaryDto.quizQuestions on the backend.
     */
    quizQuestions?: {
        pre: {
            durationId: number;
            endDate: string;
            endTime?: string;
            questions: number[];
        };
        post: {
            durationId: number;
            endDate: string;
            endTime?: string;
            questions: number[];
        };
    };
    /** When true, questions are shown in random order for participants (same as group training). */
    isRandomize?: boolean;
}

export interface QuizScheduleResponse {
    success: boolean;
    message: string;
    data?: any;
}

@Injectable({
    providedIn: 'root'
})
export class QuizScheduleService {
    private baseUrl = TRAINING_URL;

    constructor(private http: HttpClient) { }

    /**
     * Create a live quiz with the provided data
     * @param quizData - The quiz schedule data
     * @returns Observable<QuizScheduleResponse>
     */
    scheduleQuiz(quizData: QuizScheduleRequest, countryId?: number): Observable<QuizScheduleResponse> {
        let params = new HttpParams();
        if (countryId) {
            params = params.set('countryId', countryId.toString());
        }
        return this.http.post<QuizScheduleResponse>(`${this.baseUrl}/liveQuiz/create`, quizData, { params });
    }


    // create field quiz
    createFieldQuiz(quizData: any): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}/liveQuiz/createFieldQuiz`, quizData);
    }

    /** Update field training + field quiz (same body as createFieldQuiz plus trainingId). */
    updateFieldQuiz(quizData: any): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}/liveQuiz/updateFieldQuiz`, quizData);
    }

    /** Replace an entire field-quiz batch (delete all by batchId + recreate). */
    replaceFieldQuizBatch(quizData: any): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}/liveQuiz/replaceFieldQuizBatch`, quizData);
    }

    /**
     * Get list of scheduled quizzes with pagination and search
     * @param pageIndex - Page index (default: 1)
     * @param pageSize - Number of items per page (default: 10)
     * @param searchFilter - Search term for filtering by title
     * @param startDate - Filter by start date
     * @param endDate - Filter by end date
     * @param quizType - Filter by quiz type (Pre Test, Post Test, Regular Quiz)
     * @param quizSource - Filter by quiz source (Training, Quiz)
     * @returns Observable<{data: any[], paginatorValue: any}>
     */
    getScheduledQuizzesList(
        pageIndex: number = 1,
        pageSize: number = 10,
        searchFilter?: string,
        startDate?: string,
        endDate?: string,
    ): Observable<{ data: any[], paginatorValue: any }> {
        let queryParams = new HttpParams()
            .set('pageIndex', pageIndex.toString())
            .set('pageSize', pageSize.toString());

        if (searchFilter && searchFilter.trim() !== '') {
            queryParams = queryParams.set('searchFilter', searchFilter);
        }

        if (startDate) {
            queryParams = queryParams.set('startDate', startDate);
        }

        if (endDate) {
            queryParams = queryParams.set('endDate', endDate);
        }

        return this.http.get<{ data: any[], paginatorValue: any }>(`${this.baseUrl}/liveQuiz/getList`, {
            params: queryParams
        });
    }

    /**
     * Get a single quiz by ID
     * @param quizId - The quiz ID
     * @returns Observable<any>
     */
    getQuizById(quizId: number): Observable<any> {
        return this.http.get<any>(`${this.baseUrl}/liveQuiz/getById/${quizId}`);
    }

    /**
     * Update a live quiz
     * @param quizId - The quiz ID
     * @param quizData - The quiz schedule data
     * @param countryId - Required for roles with country selection (e.g. CA Trainer); ignored for single-country roles (backend uses JWT)
     * @returns Observable<QuizScheduleResponse>
     */
    updateQuiz(quizId: number, quizData: QuizScheduleRequest, countryId?: number): Observable<QuizScheduleResponse> {
        let params = new HttpParams();
        if (countryId) {
            params = params.set('countryId', countryId.toString());
        }
        return this.http.put<QuizScheduleResponse>(`${this.baseUrl}/liveQuiz/update/${quizId}`, quizData, { params });
    }

    /**
     * Delete a live quiz
     * @param quizId - The quiz ID
     * @param countryId - Required for roles with country selection (e.g. CA Trainer); ignored for single-country roles (backend uses JWT)
     * @returns Observable<QuizScheduleResponse>
     */
    deleteQuiz(quizId: number, countryId?: number): Observable<QuizScheduleResponse> {
        let params = new HttpParams();
        if (countryId) {
            params = params.set('countryId', countryId.toString());
        }
        return this.http.delete<QuizScheduleResponse>(`${this.baseUrl}/liveQuiz/delete/${quizId}`, { params });
    }
}
